<script lang="ts" setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import OmniTable from "@/components/omni/OmniTable.vue";
import {
  type CheckCallDTO,
  CheckCallType,
  type CreateCheckCallRequest,
  DeviationStatus,
} from "@/api/trips/dto/check-call";
import { FilterUtils } from "@/services/filters";
import { API } from "@/api/api";
import { useAppStore } from "@/store/app.store";
import DeviationStatusLabel from "@/views/trips/components/DeviationStatusLabel.vue";
import type { RouteStopDto, TripOverview, TripOverviewRoute } from "@/api/trips/dto/trip.dto";
import { OrderStopType } from "@/constants/order";
import FacilityInfo from "@/views/trips/components/FacilityInfo.vue";
import { OrdersMixin } from "@/views/orders/mixins/orders.mixin";
import CreateCheckCallForm from "@/views/trips/components/forms/CreateCheckCallForm.vue";
import CheckCallDateTime from "@/views/trips/view/components/CheckCallDateTime.vue";
import { useSnackbarStore } from "@/store/snackbar.store";
import { usePipStore } from "@/store/pip.store";
import { useDateTime } from "@/composables";
import dayjs from "dayjs";
import DismissCheckCallDialog from "@/views/trips/view/components/dialogs/DismissCheckCallDialog.vue";
import TripIntervalDialog from "@/views/trips/components/TripIntervalDialog.vue";
import DismissedChip from "@/components/chips/DismissedChip.vue";
import { useError } from "@/composables/useError";
import TooltipButton from "@/components/buttons/TooltipButton.vue";
import TripTimer from "@/views/trips/components/TripTimer.vue";
import type { DisabledRule } from "@/types/validation";
import { useDisableRules } from "@/composables/reactive/useDisableRules";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useI18n } from "vue-i18n";
import VerticalDotsButton from "@/components/buttons/VerticalDotsButton.vue";

const tableHeaders = [
  { title: "Type", key: "type", sortable: false },
  { title: "Location", key: "locationAddress", class: "text-grey", sortable: false },
  { title: "Miles from previous check call", key: "previousCallDistance", sortable: false },
  { title: "Miles from last stop", key: "previousStopDistance", sortable: false },
  { title: "Miles left to next stop", key: "nextStopDistance", sortable: false },
  { title: "Average speed, m/h", key: "avgSpeed", sortable: false },
  { title: "Date time", key: "dateTime", sortable: false },
  { title: "Created at, by", key: "createdAt", sortable: false },
  { title: "Actions", key: "actions", sortable: false, align: "center" },
];

const api: API = new API();
const filters = new FilterUtils();
const { dateTZ } = useDateTime();
const { showAPIError } = useError();
const { t } = useI18n();
const orderUtils = OrdersMixin.methods!;

const emits = defineEmits<{
  (e: "update"): void;
}>();

const route = useRoute();
const appStore = useAppStore();
const pipStore = usePipStore();
const { showSnackbarSuccess, showSnackbarError } = useSnackbarStore();
const { formatTimeDiff } = useDateTime();
const { createAddCheckCallDisableRules } = useDisableRules();

const checkCalls = ref<CheckCallDTO[]>([]);
const showAddCheckCallDialog = ref<boolean>(false);
const showDismissDialog = ref<boolean>(false);
const dismissCheckCallId = ref<number | null>(null);
const rejectDismissReason = ref<string>("");
const showIntervalDialog = ref<boolean>(false);
const creationLoading = ref<boolean>(false);
const checkCallPreview = ref<CheckCallDTO | null>(null);

const props = defineProps<{
  trip: TripOverview;
}>();

const routeId = computed<number>(() => parseInt(route.params.routeId.toString()));
const tripId = computed(() => route.params.id.toString());
const currentRoute = computed<TripOverviewRoute | undefined>(() =>
  props.trip.routes?.find((route) => route.id === routeId.value),
);
const nextStop = computed<RouteStopDto | undefined>(() => {
  const currentRoute = props.trip.routes?.find((route) => route.id === routeId.value);
  return currentRoute?.routeStops.find((stop: RouteStopDto) => stop.id === currentRoute.nextStopId);
});

const routeStopCheckCalls = computed(() => {
  return checkCalls.value.filter((checkCall) => checkCall.routeId === routeId.value);
});

const truck = computed(() => {
  return props.trip.routes?.find((route) => route.id === routeId.value)?.truck;
});

const ongoingETA = computed(() => {
  return checkCalls.value[0]?.eta;
});

const isETALaterThanNextStop = computed(() => {
  return dayjs(ongoingETA.value).isAfter(dayjs(nextStop.value?.timeTo));
});

const classETA = computed(() => {
  return isETALaterThanNextStop.value ? "text-danger border-danger" : "text-success border-success";
});

const buttonDisabledRules = computed<DisabledRule[]>(() => {
  return createAddCheckCallDisableRules(
    Boolean(currentRoute.value?.dispatchConfirmed),
    nextStop,
    currentRoute.value!.status,
  );
});

onMounted(() => {
  loadCheckCalls();
});

function loadCheckCalls(): void {
  api.trips
    .getCheckCalls(tripId.value)
    .then((response) => {
      if (response.success) {
        checkCalls.value = (response.data?.checkCalls || []).slice().reverse();
      }
    })
    .catch(() => {
      showSnackbarError("Could not load check calls");
    });
}

function onSuccessCreation(newCheckCall: CheckCallDTO) {
  checkCalls.value.unshift(newCheckCall);
}

function getTypeLabel(type: CheckCallType): string {
  switch (type) {
    case CheckCallType.checkCall:
      return "Check call";
    case CheckCallType.dispatchConfirmed:
      return "Dispatch confirmed";
    case CheckCallType.checkedIn:
      return "Checked in";
    case CheckCallType.load:
      return "Loaded";
    case CheckCallType.unload:
      return "Unloaded";
    case CheckCallType.loadVerify:
      return "Loaded and verified";
    case CheckCallType.unloadVerify:
      return "Unloaded and verified";
    case CheckCallType.goodToGo:
      return "Good to go";
    default:
      return "";
  }
}

function getDeviationStatusClass(deviationStatus: DeviationStatus, deletedBy: null | number): string {
  if (deletedBy) {
    return "text-grey-darken-2";
  }

  switch (deviationStatus) {
    case DeviationStatus.onTrack:
      return "text-success";
    case DeviationStatus.deviated:
      return "text-warning";
    case DeviationStatus.wrongWay:
      return "text-error";
    default:
      return "";
  }
}

function checkIsCheckCallInFuture(lastTime: string) {
  return dayjs(lastTime).isAfter(dayjs().add(1, "minute"));
}

function getRowClass(row: Record<string, string> & { item: CheckCallDTO }): string {
  if (row.item.deletedBy) {
    return "bg-grey-lighten-2";
  }

  if (checkIsCheckCallInFuture(row.item.checkCallTime)) {
    return "bg-indigo-lighten-5";
  }

  return "";
}

function checkCallProps(row: Record<string, string> & { item: CheckCallDTO }): Record<string, string> {
  return {
    class: getRowClass(row),
  };
}

function isFacilityChanged(checkCall: CheckCallDTO): boolean {
  const reversedCheckCalls = checkCalls.value.slice().reverse();
  const index = reversedCheckCalls.findIndex((call: CheckCallDTO) => call.id === checkCall.id);
  return (
    checkCall.type !== CheckCallType.goodToGo &&
    index > 0 &&
    reversedCheckCalls[index - 1].facilityId !== checkCall.facilityId
  );
}

function getStopIcon(stopType?: OrderStopType): string | undefined {
  switch (stopType) {
    case OrderStopType.pickup:
      return "mdi-package-variant-closed";
    case OrderStopType.delivery:
      return "mdi-map-marker-radius-outline";
  }
}

function getStopIconColor(stopType?: OrderStopType): string | undefined {
  switch (stopType) {
    case OrderStopType.pickup:
      return "success";
    case OrderStopType.delivery:
      return "error";
  }
}

function getStopName(stop?: RouteStopDto): string {
  let name = "";
  switch (stop?.type) {
    case OrderStopType.pickup:
      name += "Pick up";
      break;
    case OrderStopType.delivery:
      name += "Delivery";
      break;
  }
  let pickUpNumber = 1,
    deliveryNumber = 1;
  for (const route of props.trip?.routes || []) {
    for (const routeStop of route.routeStops) {
      if (routeStop.type === OrderStopType.pickup) {
        if (routeStop.id === stop?.id) {
          name += ` #${pickUpNumber}`;
          break;
        }
        pickUpNumber++;
      } else if (routeStop.type === OrderStopType.delivery) {
        if (routeStop.id === stop?.id) {
          name += ` #${deliveryNumber}`;
          break;
        }
        deliveryNumber++;
      }
    }
  }

  return name;
}

const addCheckCallProps = reactive({
  get routeId() {
    return currentRoute.value?.id ?? 0;
  },
  get routeStops() {
    return currentRoute.value?.routeStops ?? [];
  },
  get creationLoading() {
    return creationLoading.value;
  },
  get checkCallPreview() {
    return checkCallPreview.value;
  },
});

function openAddCheckCallPip(): void {
  if (!currentRoute.value?.id) {
    return;
  }

  pipStore.open({
    component: CreateCheckCallForm,
    props: addCheckCallProps,
    on: {
      create: createCheckCall,
      close: () => {
        showAddCheckCallDialog.value = false;
      },
    },
  });
}

function showAddCheckCall(): void {
  showAddCheckCallDialog.value = true;
}

watch(showAddCheckCallDialog, (show) => {
  if (show) {
    checkCallPreview.value = null;
    openAddCheckCallPip();
  } else {
    pipStore.close();
  }
});

function getNextStopMilesLeftLabel(type: CheckCallType): string {
  const emptyLabelTypes = [CheckCallType.dispatchConfirmed, CheckCallType.goodToGo];

  return emptyLabelTypes.includes(type) ? "" : "miles";
}

async function updateRouteInterval(interval: number | null): Promise<void> {
  if (!props.trip.id) {
    return;
  }

  try {
    const response = await api.trips.updateRouteInterval(props.trip.id, routeId.value, interval);

    if (response.success) {
      showSnackbarSuccess("Interval saved successfully");
      loadCheckCalls();
      emits("update");
    }
  } catch (error) {
    console.log(error);
    showSnackbarError("Could not save the trip interval");
  }
}

function getElapsedTime(lasTime: string) {
  return formatTimeDiff(lasTime);
}

function openDismissDialog(id: number) {
  showDismissDialog.value = true;
  dismissCheckCallId.value = id;
}

function canDismiss(item: CheckCallDTO) {
  if (item.type !== CheckCallType.checkCall) {
    rejectDismissReason.value = "System check calls can’t be dismissed from this page";
    return false;
  }

  const isNewest =
    checkCalls.value.filter((check) => !check.deletedBy).findIndex((checkCall) => checkCall.id === item.id) === 0;

  if (!isNewest) {
    rejectDismissReason.value = "Only last active check call in route can be dismissed";
  }

  return isNewest;
}

async function createCheckCall(data: CreateCheckCallRequest) {
  if (!currentRoute.value?.id) {
    return;
  }

  creationLoading.value = true;

  try {
    const response = await api.events.createCheckCall(currentRoute.value.id, data);

    if (!response.success || !response.data) {
      showAPIError(response, "Error creating check call");

      return;
    }
    if (data.commit) {
      onSuccessCreation(response.data);
    } else {
      checkCallPreview.value = response.data;
    }
  } catch (error) {
    console.error(error);
  } finally {
    creationLoading.value = false;
  }
}
</script>

<template>
  <div class="d-flex align-center justify-space-between mb-4">
    <div class="font-size-16 font-weight-500 text-grey-darken-3">Check calls</div>

    <div class="d-flex align-center">
      <div class="d-flex align-center mr-8">
        <span class="text-grey-darken-1">New check call in:</span>
        <tooltip-button :rules="buttonDisabledRules">
          <template #default="{ disabled }">
            <trip-timer
              :disabled="disabled"
              :interval="currentRoute?.checkCallInterval || null"
              :next-update-time="currentRoute?.nextCheckCallTime || null"
              :truck-id="truck?.id"
              :truck-number="truck?.number"
              class="ml-3"
              @open-dialog="showIntervalDialog = true"
            ></trip-timer>
          </template>
        </tooltip-button>
      </div>

      <tooltip-button :rules="buttonDisabledRules">
        <template #default="{ disabled }">
          <v-btn
            :disabled="disabled"
            class="font-weight-600 text-uppercase"
            color="primary"
            prepend-icon="mdi-plus"
            variant="flat"
            @click="showAddCheckCall"
          >
            Add check call
          </v-btn>
        </template>
      </tooltip-button>
    </div>
  </div>
  <omni-table
    :headers="tableHeaders"
    :items="routeStopCheckCalls"
    :items-per-page="-1"
    :row-props="checkCallProps"
    :server-data="false"
    show-all-option
  >
    <template #[`header.previousCallDistance`]="{ column }: { column?: { title?: string | undefined } }">
      {{ column?.title }}
      <v-tooltip>
        <template #activator="{ props: tooltipProps }">
          <v-icon class="ml-3" icon="mdi-information-outline" size="16" v-bind="tooltipProps"></v-icon>
        </template>
        Distance indicating how much the truck deviates from<br />
        its optimal route since the last check call
      </v-tooltip>
    </template>
    <template #[`header.previousStopDistance`]="{ column }">
      {{ column.title }}
      <v-tooltip>
        <template #activator="{ props: tooltipProps }">
          <v-icon class="ml-3" icon="mdi-information-outline" size="16" v-bind="tooltipProps"></v-icon>
        </template>
        Distance indicating how much the truck deviates from its<br />
        optimal route since the last stop
      </v-tooltip>
    </template>
    <template #[`header.nextStopDistance`]="{ column }">
      {{ column.title }}
      <v-tooltip>
        <template #activator="{ props: tooltipProps }">
          <v-icon class="ml-3" icon="mdi-information-outline" size="16" v-bind="tooltipProps"></v-icon>
        </template>
        The distance indicating how much closer the truck has become<br />
        to the next stop since the last check call.
      </v-tooltip>
    </template>

    <template #[`item.type`]="{ item }: { item: CheckCallDTO }">
      <v-tooltip :disabled="!item.deletedBy">
        <template #activator="{ props: tooltipProps }">
          <div class="d-flex align-center" v-bind="tooltipProps">
            <v-icon
              v-if="getStopIcon(item.routeStopType)"
              :color="getStopIconColor(item.routeStopType)"
              :icon="getStopIcon(item.routeStopType)"
              class="mr-2"
              size="20"
            ></v-icon>
            <div class="text-grey-darken-3" data-qa="event-name">{{ getTypeLabel(item.type) }}</div>
            <v-chip
              v-if="checkIsCheckCallInFuture(item.checkCallTime)"
              :class="`ml-3  ${item.deletedBy ? 'bg-grey-darken-2' : 'bg-indigo-lighten-2'}`"
              color="white"
              size="x-small"
              >For Future - in {{ getElapsedTime(item.checkCallTime) }}
            </v-chip>
            <dismissed-chip v-else-if="item.deletedBy" class="ml-3" />
          </div>
        </template>

        Check call has been dismissed
      </v-tooltip>
    </template>
    <template #[`item.locationAddress`]="{ item }: { item: CheckCallDTO }">
      <div class="d-flex align-center">
        <div class="text-grey-darken-3 font-weight-500" data-qa="check-calls-tab-location">
          {{ item.locationAddress }}
        </div>
        <div v-if="isFacilityChanged(item)" class="d-flex align-center font-size-12 text-warning ml-4">
          <v-icon icon="mdi-alert-circle-outline"></v-icon>
          <div class="ml-2">Facility has<br />been updated</div>
        </div>
      </div>
    </template>
    <template #[`item.previousCallDistance`]="{ item }: { item: CheckCallDTO }">
      <template v-if="item.previousCallDistance">
        <span class="text-grey-darken-3 font-weight-500" data-qa="miles-from-previous-cc-total">
          {{ filters.miles(item.previousCallDistance, 0) }} miles
        </span>
        <span
          :class="getDeviationStatusClass(item.currentDeviationStatus!, item.deletedBy)"
          class="font-weight-500"
          data-qa="miles-from-previous-cc-extra"
        >
          ({{ filters.miles(item.currentDeviation, 0) }})
        </span>

        <v-tooltip :disabled="!item.deletedBy">
          <template #activator="{ props: tooltipProps }">
            <deviation-status-label
              v-if="item.currentDeviationStatus"
              :deviation-status="item.currentDeviationStatus"
              :is-dismissed="Boolean(item.deletedBy)"
              class="ml-3"
              data-qa="miles-from-previous-cc-status"
              v-bind="tooltipProps"
            ></deviation-status-label>
          </template>

          Check call has been dismissed
        </v-tooltip>
      </template>
      <template v-else> -</template>
    </template>
    <template #[`item.previousStopDistance`]="{ item }: { item: CheckCallDTO }">
      <template v-if="item.previousStopDistance">
        <span class="text-grey-darken-3 font-weight-500" data-qa="miles-from-last-stop-total">
          {{ filters.miles(item.previousStopDistance, 0) }} miles
        </span>
        <span
          :class="getDeviationStatusClass(item.accumulatedDeviationStatus!, item.deletedBy)"
          class="font-weight-500"
          data-qa="miles-from-last-stop-extra"
        >
          ({{ filters.miles(item.accumulatedDeviation, 0) }})
        </span>
        <v-tooltip :disabled="!item.deletedBy">
          <template #activator="{ props: tooltipProps }">
            <deviation-status-label
              v-if="item.accumulatedDeviationStatus"
              :deviation-status="item.accumulatedDeviationStatus"
              :is-dismissed="Boolean(item.deletedBy)"
              class="ml-3"
              data-qa="miles-from-last-stop-status"
              v-bind="tooltipProps"
            ></deviation-status-label>
          </template>

          Check call has been dismissed
        </v-tooltip>
      </template>
      <template v-else> -</template>
    </template>
    <template #[`item.nextStopDistance`]="{ item }: { item: CheckCallDTO }">
      <template v-if="item.nextStopDistance || item.type === CheckCallType.checkedIn">
        <span class="text-grey-darken-3 font-weight-500" data-qa="miles-left-next-stop-total">
          {{ filters.miles(item.nextStopDistance, 0) }}
        </span>
        <span
          :class="{
            'text-grey-darken-2': item.deletedBy,
            'text-success': item.distanceReducing! < 0 && !item.deletedBy,
            'text-error': item.distanceReducing! >= 0 && !item.deletedBy,
          }"
          class="ml-2 font-weight-500"
          data-qa="miles-left-next-stop-extra"
        >
          {{ filters.miles(item.distanceReducing, 0) }} {{ getNextStopMilesLeftLabel(item.type) }}
        </span>
      </template>
      <template v-else> -</template>
    </template>
    <template #[`item.avgSpeed`]="{ item }: { item: CheckCallDTO }">
      <span class="text-grey-darken-3 font-weight-500" data-qa="avg-speed">{{ filters.miles(item.avgSpeed, 0) }}</span>
    </template>
    <template #[`item.dateTime`]="{ item }: { item: CheckCallDTO }">
      <check-call-date-time :check-call="item" />
    </template>
    <template #[`item.createdAt`]="{ item }: { item: CheckCallDTO }">
      <div class="text-grey-darken-3" data-qa="created-at-date">
        {{ dateTZ(item.createdAt, appStore.dispatcher!.timezone) }}
      </div>
      <div
        v-if="item.createdDispatcher"
        class="text-primary font-size-12 font-weight-500"
        data-qa="cc-tab-dispatcher-name"
      >
        {{ item.createdDispatcher.name }} (Dispatcher)
      </div>
      <router-link
        v-else-if="item.createdUser"
        :to="{ name: 'user-view', params: { id: item.createdUser.id } }"
        class="text-primary font-size-12 font-weight-500 text-decoration-none"
        target="_blank"
      >
        {{ item.createdUser.name }} (User)
      </router-link>
      <div v-else class="text-primary font-size-12 font-weight-500">System</div>
    </template>
    <template #[`item.actions`]="{ item }: { item: CheckCallDTO }">
      <v-menu>
        <template #activator="{ props: menuProps }">
          <vertical-dots-button v-bind="menuProps" />
        </template>

        <permission-tooltip :permission="DispatcherPermissionValue.CheckCallsEdit">
          <template #activator="{ disabled }">
            <v-list :disabled="disabled">
              <v-tooltip :disabled="canDismiss(item)" location="top">
                <template #activator="{ props: tooltipProps }">
                  <div v-bind="tooltipProps">
                    <v-list-item :disabled="!canDismiss(item)" @click="openDismissDialog(item.id)">
                      <v-list-item-title>
                        <v-icon class="mr-2">mdi-close</v-icon>
                        <span class="font-size-16">Dismiss check call</span>
                      </v-list-item-title>
                    </v-list-item>
                  </div>
                </template>

                {{ rejectDismissReason }}
              </v-tooltip>
            </v-list>
          </template>
          {{ t("permission.dismiss-check-call") }}
        </permission-tooltip>
      </v-menu>
    </template>
    <template #bottom>
      <div v-if="nextStop" class="bg-green-lighten-5 d-flex justify-space-between bottom-row">
        <div class="text-blue-grey-darken-1 font-weight-500">Next stop</div>
        <div>
          <div class="d-flex align-center mb-2">
            <v-icon
              v-if="getStopIcon(nextStop!.type)"
              :color="getStopIconColor(nextStop!.type)"
              :icon="getStopIcon(nextStop!.type)"
              class="mr-2"
              size="20"
            ></v-icon>
            <div class="text-blue-grey-darken-1 font-weight-500" data-qa="next-stop-type">
              {{ getStopName(nextStop) }}
            </div>
          </div>
          <router-link
            :to="{ name: 'order-view', params: { id: nextStop?.order?.id } }"
            class="text-decoration-none text-primary font-size-12 font-weight-500"
            data-qa="order-number"
            target="_blank"
          >
            Order #{{ nextStop?.order?.number }}
          </router-link>
        </div>
        <facility-info v-if="nextStop?.facility" :facility="nextStop.facility"></facility-info>
        <div>
          <div class="text-grey-darken-2 mb-2">Time:</div>
          <div class="d-flex text-grey-darken-3 font-weight-500">
            <div class="mr-1" data-qa="next-stop-time-type">{{ orderUtils.getTimeType(nextStop!.timeType) }}:</div>
            <div>
              <div data-qa="next-stop-time-1">
                {{ orderUtils.getTimeInterval(nextStop!.timeFrom, nextStop!.timeTo, appStore.dispatcher!.timezone) }}
              </div>
              <div data-qa="next-stop-time-2">
                {{ orderUtils.getTimeInterval(nextStop!.timeFrom, nextStop!.timeTo, nextStop!.timezone) }}
              </div>
            </div>
          </div>
          <div v-if="nextStop?.time2From" class="d-flex text-grey-darken-3 font-weight-500">
            <div class="mr-1">{{ orderUtils.getTimeType(nextStop!.time2Type!) }}:</div>
            <div>
              <div>
                {{ orderUtils.getTimeInterval(nextStop!.time2From, nextStop!.time2To, appStore.dispatcher!.timezone) }}
              </div>
              <div>{{ orderUtils.getTimeInterval(nextStop!.time2From, nextStop!.time2To, nextStop!.timezone) }}</div>
            </div>
          </div>
        </div>
        <div v-if="ongoingETA">
          <div class="text-grey-darken-2 mb-2">ETA:</div>
          <div :class="classETA" class="rounded py-1 px-2 font-weight-500">
            <div data-qa="next-stop-eta-time-1">
              {{ dateTZ(ongoingETA, appStore.dispatcher?.timezone) }}
            </div>
            <div data-qa="next-stop-eta-time-2">{{ dateTZ(ongoingETA, nextStop.timezone) }}</div>
          </div>
        </div>
      </div>
    </template>
  </omni-table>

  <dismiss-check-call-dialog
    v-if="dismissCheckCallId"
    v-model="showDismissDialog"
    :check-call-id="dismissCheckCallId"
    :route-id="routeId"
    @update="loadCheckCalls"
  ></dismiss-check-call-dialog>

  <trip-interval-dialog
    v-model="showIntervalDialog"
    :interval="currentRoute?.checkCallInterval || null"
    :truck-id="currentRoute?.truck?.id"
    :truck-number="currentRoute?.truck?.number"
    title="Route timer settings"
    @update-interval="updateRouteInterval"
  />
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.bottom-row {
  padding: 7px 16px;
  border: 1px solid $grey-lighter;
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}
</style>
